import React, { useMemo } from 'react';
import styled from 'styled-components';
import { DataUsageType } from '../modules/cameras/api/dataUsage/types';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Filler
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

export type OverviewDataProps = {
  data: DataUsageType;
  onMonthChange: (month: string) => void;
  planTotalGB?: number;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
);

const formatDayLabel = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.getDate().toString();
};

const formatMonthLabel = (monthStr: string) => {
  const [y, m] = monthStr.split('-');
  const d = new Date(parseInt(y), parseInt(m) - 1);
  return d.toLocaleString('default', { month: 'short', year: '2-digit' });
};

const EARLIEST_MONTH = new Date(2026, 2, 1); // March 2026

const getMonthOptions = () => {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    if (d < EARLIEST_MONTH) break;
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
};

export const OverviewData = ({ data, onMonthChange, planTotalGB }: OverviewDataProps) => {
  const monthOptions = useMemo(() => getMonthOptions(), []);

  const dailyLabels = data.daily.map((d) => formatDayLabel(d.date));
  const dailyValues = data.daily.map((d) => d.dailyGB);
  const cumulativeValues = data.daily.map((d) => d.cumulativeGB);

  // Diagonal expected-pace line: 0 → planTotalGB across the month
  const expectedPaceLine = useMemo(() => {
    if (!planTotalGB || data.daily.length === 0) return null;
    const [y, m] = data.month.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    return data.daily.map((d) => {
      const day = new Date(d.date + 'T00:00:00').getDate();
      return parseFloat(((planTotalGB * day) / daysInMonth).toFixed(4));
    });
  }, [planTotalGB, data.daily, data.month]);

  const overageWarning = useMemo(() => {
    if (!planTotalGB || data.daily.length === 0) return null;
    // Only show warning for the current month
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (data.month !== currentMonth) return null;
    const latestCumulative = data.daily[data.daily.length - 1].cumulativeGB;
    const latestDay = new Date(data.daily[data.daily.length - 1].date + 'T00:00:00').getDate();
    if (latestDay === 0 || latestCumulative === 0) return null;
    const [y, m] = data.month.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const dailyRate = latestCumulative / latestDay;
    const projectedGB = dailyRate * daysInMonth;
    if (projectedGB > planTotalGB) {
      // Work out which day of the month the plan limit is hit
      const runOutDay = Math.ceil(planTotalGB / dailyRate);
      const runOutDate = new Date(y, m - 1, Math.min(runOutDay, daysInMonth));
      const formatted = runOutDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
      });
      return { runOutDate: formatted };
    }
    return null;
  }, [planTotalGB, data.daily, data.month]);

  const datasets: any[] = [
    {
      type: 'bar' as const,
      label: 'Daily usage (GB)',
      data: dailyValues,
      backgroundColor: 'rgba(41, 168, 211, 0.7)',
      borderRadius: 4,
      order: 3
    },
    {
      type: 'line' as const,
      label: 'Cumulative (GB)',
      data: cumulativeValues,
      borderColor: '#0F9D58',
      backgroundColor: 'rgba(15, 157, 88, 0.08)',
      fill: true,
      tension: 0.3,
      pointRadius: 2,
      borderWidth: 2,
      order: 1
    }
  ];

  if (expectedPaceLine) {
    datasets.push({
      type: 'line' as const,
      label: `Expected pace (${planTotalGB} GB plan)`,
      data: expectedPaceLine,
      borderColor: 'rgba(220, 80, 80, 0.7)',
      borderDash: [8, 4],
      borderWidth: 2,
      pointRadius: 0,
      fill: false,
      tension: 0,
      order: 2
    });
  }

  const dailyChartData = {
    labels: dailyLabels,
    datasets
  };

  const dailyOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        grid: { display: false },
        title: { display: true, text: 'Day of month', font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: 'GB', font: { size: 11 } },
        ticks: { callback: (v) => v + ' GB' }
      }
    },
    plugins: {
      legend: { position: 'bottom', labels: { boxHeight: 8, usePointStyle: true, padding: 16 }, onClick: () => {} },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items[0]?.dataIndex;
            return idx !== undefined ? data.daily[idx]?.date : '';
          },
          label: (item) => `${item.dataset.label}: ${Number(item.raw).toFixed(2)} GB`
        }
      }
    }
  };

  // Monthly history bar chart
  const monthlyLabels = data.monthly.map((m) => formatMonthLabel(m.month));
  const monthlyValues = data.monthly.map((m) => m.totalGB);

  const monthlyChartData = {
    labels: monthlyLabels,
    datasets: [
      {
        label: 'Monthly total (GB)',
        data: monthlyValues,
        backgroundColor: data.monthly.map((m) =>
          m.month === data.month ? 'rgba(41, 168, 211, 0.85)' : 'rgba(41, 168, 211, 0.35)'
        ),
        borderRadius: 4
      }
    ]
  };

  const monthlyOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => v + ' GB' }
      }
    },
    plugins: {
      legend: { display: false, onClick: () => {} },
      tooltip: { callbacks: { label: (item) => `${Number(item.raw).toFixed(2)} GB` } }
    }
  };

  return (
    <Container>
      <Header>
        <MonthSelect
          value={data.month}
          onChange={(e) => onMonthChange(e.target.value)}
        >
          {monthOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </MonthSelect>
        <TotalBadge>{data.totalGB.toFixed(2)} GB this month</TotalBadge>
      </Header>

      {overageWarning && (
        <WarningBanner>
          ⚠️ At the current rate you'll run out of data on <strong>{overageWarning.runOutDate}</strong>. Consider reducing usage or upgrading your plan.
        </WarningBanner>
      )}

      {data.daily.length === 0 ? (
        <EmptyState>No usage records available for this month.</EmptyState>
      ) : (
        <ChartCard>
          <ChartTitle>Daily Usage</ChartTitle>
          <ChartWrap>
            {/* @ts-ignore - mixed chart types */}
            <Chart type="bar" data={dailyChartData} options={dailyOptions} />
          </ChartWrap>
        </ChartCard>
      )}

      {data.monthly.length > 1 && (
        <ChartCard>
          <ChartTitle>Monthly History</ChartTitle>
          <SmallChartWrap>
            <Chart type="bar" data={monthlyChartData} options={monthlyOptions} />
          </SmallChartWrap>
        </ChartCard>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const MonthSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray900};
  font-size: ${({ theme }) => theme.fontSize.small};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
  }
`;

const TotalBadge = styled.span`
  background: ${({ theme }) => theme.colors.primary100};
  color: ${({ theme }) => theme.colors.primary};
  padding: 0.4rem 0.75rem;
  border-radius: 1rem;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.small};
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray900};
  padding: 1rem;
`;

const ChartTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: 600;
`;

const ChartWrap = styled.div`
  position: relative;
  height: 280px;
  @media (max-width: 850px) {
    height: 220px;
  }
`;

const SmallChartWrap = styled.div`
  position: relative;
  height: 180px;
  @media (max-width: 850px) {
    height: 150px;
  }
`;

const WarningBanner = styled.div`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.medium};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray900};
`;
